/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react';
import dayjs from 'dayjs';
import { Card, Table, Button } from '@avila-tek/ui/src';
import { useRouter } from 'next/router';
import { createColumnHelper } from '@tanstack/react-table';
import { Hit } from '@algolia/client-search';
import { Company, PageInfo, Pagination, User } from '../../models';
import { ActiveDotCell } from '../shared';
import { useAlgoliaSearch, useUser } from '../../hooks';
import { roles } from '../../static';
import { AlgoliaHitToObject } from '../../lib';

const columnHelper = createColumnHelper<User>();

const columns = [
  // @ts-ignore library has a issue: https://github.com/TanStack/table/issues/4224
  columnHelper.accessor('firstName', {
    header: 'Nombre',
    cell: (info) =>
      `${info.row.original.firstName} ${info.row.original.lastName}`,
  }),
  columnHelper.accessor('email', {
    header: 'Correo',
  }),
  columnHelper.accessor('company', {
    header: 'Empresa',
    cell: (info) => `${(info.getValue() as Company)?.name ?? ''}`,
  }),
  columnHelper.accessor('createdAt', {
    header: 'Ingreso',
    cell: (info) => `${dayjs(info.getValue()).format('DD/MM/YYYY')}`,
  }),
  columnHelper.accessor('active', {
    header: 'Estatus',
    cell: (info) => <ActiveDotCell active={info?.getValue()} />,
  }),
];

interface ClientTableProps {
  data: Pagination<User>;
  pagination: Partial<PageInfo>;
  setPagination: React.Dispatch<React.SetStateAction<Partial<PageInfo>>>;
}

function ClientTable({ data, pagination, setPagination }: ClientTableProps) {
  const router = useRouter();
  const [user] = useUser();

  const [searchText, setSearchText] = React.useState<string | number>('');
  const [clientsData, setClientsData] = React.useState(data.items);
  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const { fetchData } = useAlgoliaSearch<User>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: [
      'firstName',
      'email',
      'lastName',
      'dni',
      'company.name',
      'company._id',
    ],
    index: 'users',
    filters:
      user.userType === roles.admin
        ? `userType:client`
        : `userType:client AND company._id:${(user?.company as Company)?._id}`,
  });

  React.useEffect(() => {
    async function syncData() {
      if (searchText) {
        const { items } = await fetchData({
          searchText: searchText.toString(),
          newPage: pageInfoAlgolia.currentPage,
        });
        setClientsData(AlgoliaHitToObject<User>(items as Hit<User>[]));
      } else {
        setClientsData(data.items);
      }
    }
    syncData();
  }, [pageInfoAlgolia.currentPage, searchText, pagination]);

  const handleGoToPage = (value: number) => {
    if (searchText) {
      setPageInfoAlgolia({ ...pageInfoAlgolia, currentPage: value - 1 });
    } else {
      setPagination({ ...pagination, currentPage: value });
    }
  };

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    router.push(
      `/app/${user.userType === roles.admin ? 'clients' : 'employees'}/${id}`
    );
  };

  const onClickAddButton = () => router.push('/app/employees/create');

  return (
    <Card className="overflow-x-auto p-5">
      <Table<User>
        data={clientsData}
        columns={columns}
        href="/app/company/create"
        manualPagination
      >
        <Table.Header className="p-6 w-full flex justify-between pb-10">
          <Table.SearchInput
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
          {user.userType === roles.company ? (
            <section className="relative flex flex-row gap-2">
              <Table.AddButton title="Beneficiario" action={onClickAddButton} />
              <Button
                className="whitespace-nowrap w-10"
                onClick={(e) => {
                  e.preventDefault();
                  router.push('/app/employees/assignBenefits');
                }}
              >
                Asignar beneficio
              </Button>
            </section>
          ) : null}
        </Table.Header>
        <Table.Content<User> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination
            handleGoToPage={handleGoToPage}
            paginationInfo={{
              count: data.count,
              pageInfo: searchText
                ? {
                    ...pageInfoAlgolia,
                    currentPage: pageInfoAlgolia.currentPage + 1,
                  }
                : data.pageInfo,
            }}
            className="ml-auto"
          />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default ClientTable;
